import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useInfusionIdentityVerify, useInfusionOrderDetail, type InfusionOrderView } from '@/hooks/useLab';
import { canVerifyInfusionIdentity } from '@/lib/labPermissions';

export default function InfusionIdentityVerifyPage() {
  const { infusionId } = useParams<{ infusionId: string }>();
  const { userId, permissions } = useAuth();
  const { fetch: fetchInfusion, loading: infusionLoading, error: infusionError } = useInfusionOrderDetail(infusionId!);
  const { verify, error: verifyError } = useInfusionIdentityVerify();
  const [infusion, setInfusion] = useState<InfusionOrderView | null>(null);
  const [reviewerId, setReviewerId] = useState('');
  const [enteredName, setEnteredName] = useState('');
  const [enteredIdCardNumber, setEnteredIdCardNumber] = useState('');
  const [verificationResult, setVerificationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadInfusion() {
      if (!infusionId || !canVerifyInfusionIdentity(permissions)) return;
      try {
        const data = await fetchInfusion();
        if (!cancelled) {
          setInfusion(data);
        }
      } catch {
        if (!cancelled) {
          toast.error('加载回输单详情失败');
        }
      }
    }
    loadInfusion();
    return () => {
      cancelled = true;
    };
  }, [fetchInfusion, infusionId, permissions]);

  // Check permission — wildcard-aware (mirrors backend RBAC; identity-check
  // shares the `lab.infusion:update` gate used by POST /lab/infusions/:id/verify)
  if (!canVerifyInfusionIdentity(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有执行身份核对的权限</p>
        </Card>
      </div>
    );
  }

  const handleVerify = async () => {
    if (!infusion) {
      toast.error('请先加载回输单详情');
      return;
    }

    if (infusion.status !== 'identity_check_pending') {
      toast.error('当前回输单状态不可执行身份核对');
      return;
    }

    if (!userId || !reviewerId || !enteredName || !enteredIdCardNumber) {
      toast.error('请填写所有必填项');
      return;
    }

    if (userId === reviewerId) {
      toast.error('执行人和审核人必须不同');
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      const result = await verify({
        infusionId: infusionId!,
        reviewerId,
        enteredName,
        enteredIdCardNumber,
        expectedInfusionVersion: infusion.version,
      });

      // Check for explicit success values only
      const isSuccess = result?.identityCheck?.result === 'matched';

      if (isSuccess) {
        toast.success('身份核对通过');
        setVerificationResult({ success: true, message: '身份核对已通过' });
        if (result?.infusionOrder) {
          setInfusion(result.infusionOrder);
        }
      } else {
        // Any non-explicit success is treated as failure/blocked
        const errorMsg = '身份核对失败或被阻断';
        toast.error(errorMsg);
        setVerificationResult({ success: false, message: errorMsg });
      }
    } catch (err: unknown) {
      // Backend error must be shown as-is
      const errorMsg = (err as Error)?.message || '身份核对失败';
      toast.error(errorMsg);
      setVerificationResult({ success: false, message: errorMsg });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">输液身份核对</h1>

        {/* Medical safety red line warning */}
        <Card className="mb-6 border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-900">⚠️ 医疗安全红线</p>
          <p className="text-sm text-red-800 mt-2">
            执行人与审核人必须不同。核对失败将自动创建异常事件并阻止后续操作。
          </p>
        </Card>

        {infusionLoading && (
          <Card className="mb-6 p-4">
            <p className="text-sm text-muted-foreground">正在加载回输单详情…</p>
          </Card>
        )}

        {infusionError && (
          <Card className="mb-6 border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-900">回输单加载失败</p>
            <p className="text-sm text-red-800 mt-2">{infusionError.message}</p>
          </Card>
        )}

        {/* Infusion details */}
        {infusion && (
          <Card className="mb-6 p-6">
            <h2 className="text-xl font-semibold mb-4">回输单信息</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground">回输单号</Label>
                <p className="font-mono">{infusion.infusionNo}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">客户 ID</Label>
                <p className="font-mono">{infusion.customerId}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">细胞批次 ID</Label>
                <p className="font-mono">{infusion.cellBatchId}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">当前状态</Label>
                <p>{getInfusionStatusLabel(infusion.status)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">版本号</Label>
                <p className="font-mono">{infusion.version}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Verification form */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">核对人员与患者信息</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="performer">执行人 *</Label>
              <Input
                id="performer"
                value={userId ?? ''}
                disabled
                placeholder="当前登录用户"
              />
            </div>

            <div>
              <Label htmlFor="reviewer">审核人 *</Label>
              <Input
                id="reviewer"
                value={reviewerId}
                onChange={(e) => setReviewerId(e.target.value)}
                disabled={isVerifying}
                placeholder="输入审核人ID"
              />
            </div>

            <div>
              <Label htmlFor="enteredName">患者姓名 *</Label>
              <Input
                id="enteredName"
                value={enteredName}
                onChange={(e) => setEnteredName(e.target.value)}
                disabled={isVerifying}
                placeholder="输入患者姓名"
              />
            </div>

            <div>
              <Label htmlFor="enteredIdCardNumber">身份证号码 *</Label>
              <Input
                id="enteredIdCardNumber"
                value={enteredIdCardNumber}
                onChange={(e) => setEnteredIdCardNumber(e.target.value)}
                disabled={isVerifying}
                placeholder="输入身份证号码"
              />
            </div>

            {verificationResult && (
              <div
                className={`p-4 rounded-md ${
                  verificationResult.success
                    ? 'bg-green-50 text-green-900'
                    : 'bg-red-50 text-red-900'
                }`}
              >
                <p className="font-semibold">{verificationResult.message}</p>
              </div>
            )}

            <Button
              onClick={handleVerify}
              disabled={
                isVerifying ||
                infusionLoading ||
                !infusion ||
                infusion.status !== 'identity_check_pending' ||
                !userId ||
                !reviewerId ||
                !enteredName ||
                !enteredIdCardNumber
              }
              className="w-full"
            >
              {isVerifying ? '核对中…' : '确认核对'}
            </Button>
          </div>
        </Card>

        {verifyError && (
          <Card className="mt-6 border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-900">API错误</p>
            <p className="text-sm text-red-800 mt-2">{verifyError.message}</p>
          </Card>
        )}
      </div>
    </div>
  );
}

function getInfusionStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    scheduled: '已排期',
    identity_check_pending: '待身份核对',
    identity_check_failed: '核对失败',
    ready_for_infusion: '待回输',
    infusing: '回输中',
    completed: '已完成',
    aborted: '已中止',
    cancelled: '已取消',
  };
  return labels[status] ?? status;
}
